<template>
  <aside class="mobile-sidebar flex grow flex-col bg-white">
    <header class="flex items-center justify-between p-6">
      <div>
        <NuxtLink
          :to="isLoggedIn ? '/experiences' : '/'"
          @click="closeSidebar"
        >
          <img
            class="block h-6 w-6"
            src="/ag-avatar.svg"
            alt="Approach Guides"
          />
        </NuxtLink>
      </div>
      <button
        class="-mr-2 h-8 w-8 lg:hidden"
        @click="closeSidebar"
      >
        <Icon icon="times" />
      </button>
    </header>
    <main class="flex grow flex-col justify-center">
      <NavigationMenu
        :items="items"
        :ui="{ item: 'px-6' }"
        @navigate="closeSidebar"
      />
    </main>
    <footer>
      <div class="border-t text-center text-sm font-semibold text-supplement">
        <div
          v-if="isLoggedIn && user"
          class="grid grid-cols-2"
        >
          <NuxtLink
            href="https://www.approachguides.com/contact"
            target="_blank"
            class="space-x-3 p-6 text-supplement"
            @click="closeSidebar"
          >
            <Icon
              icon="message"
              prefix="far"
            />
            <span>{{ $t("contact") }}</span>
          </NuxtLink>
          <div
            class="space-x-3 p-6"
            @click="handleLogout"
          >
            <Icon icon="arrow-right-from-bracket" />
            <span>{{ $t("logout") }}</span>
          </div>
        </div>
        <nav
          v-else
          class="grid grid-cols-2"
        >
          <NuxtLink
            :to="getUrl('login')"
            class="p-6"
            @click="closeSidebar"
          >{{ $t("sign_in") }}</NuxtLink>
          <div class="grid place-items-center">
            <Button
              :to="getUrl('signup')"
              append-icon="long-arrow-right"
              @click="closeSidebar"
            >
              {{ $t("get_started") }}
            </Button>
          </div>
        </nav>
      </div>
    </footer>
  </aside>
</template>

<script setup lang="ts">
import type { SignupSource } from '~/composables/useSignup'
import { withQuery } from 'ufo'

const { isLoggedIn, logout } = useAuth()
const { showMobileSidebar } = useLayoutState()
const route = useRoute()
const user = useUser()
const { t } = useI18n()

const items = computed(() => {
  const links = [
    {
      icon: 'home',
      label: t('home'),
      to: '/'
    },
    {
      icon: 'experience',
      label: t('experiences'),
      to: '/experiences',
      active: route.path.startsWith('/experiences')
    },
    {
      icon: 'copilot',
      label: t('co_brandable_websites'),
      to: '/websites'
    }
  ]

  if (user.value) {
    links.splice(1, 0, {
      icon: 'user',
      label: t('my_profile'),
      to: '/profile'
    })
  }

  return links
})

const closeSidebar = () => (showMobileSidebar.value = false)

const handleLogout = () => {
  logout()
  closeSidebar()
}

const { isValidSource } = useSignup()

const getUrl = (action: 'login' | 'signup') => {
  const source = route.meta.source as SignupSource
  let url = `/${action}`

  if (route.fullPath !== '/') {
    url += `?redirect=${route.fullPath}`
  }

  if (isValidSource(source)) {
    url = withQuery(url, { source })

    if (source !== 'inspires' && typeof route.params.partner_slug === 'string') {
      url = withQuery(url, { partner: route.params.partner_slug })
    }
  }

  return url
}
</script>
